import { useState, useEffect, useCallback } from 'react';
import i18n, { changeLanguage, loadTranslations } from '../utils/i18n';

export const useTranslation = () => {
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Initialize with saved language preference or current i18n language
    return localStorage.getItem('language') || i18n.language || 'en';
  });

  // Memoize t so it only gets a new reference when translations actually change,
  // preventing useEffect([t]) consumers from looping on every render.
  const t = useCallback((key) => {
    if (!i18n.isInitialized) {
      return key;
    }
    return i18n.t(key, { defaultValue: key });
  }, [updateTrigger, currentLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleLanguageChange = async (lng) => {
      setCurrentLanguage(lng);
      // Force re-render to update translations
      setUpdateTrigger(prev => prev + 1);
    };

    const handleTranslationsLoaded = (lang) => {
      // Update when translations are loaded for current language
      if (lang === currentLanguage || lang === i18n.language) {
        setUpdateTrigger(prev => prev + 1);
      }
    };

    // Sync with current i18n language on mount
    if (i18n.language && i18n.language !== currentLanguage) {
      setCurrentLanguage(i18n.language);
    }

    // Wait for i18n to be initialized
    if (i18n.isInitialized) {
      setUpdateTrigger(prev => prev + 1);
    } else {
      const initializedHandler = () => {
        setUpdateTrigger(prev => prev + 1);
        // Update language state when i18n is initialized
        if (i18n.language) {
          setCurrentLanguage(i18n.language);
        }
      };
      i18n.on('initialized', initializedHandler);
    }

    i18n.on('languageChanged', handleLanguageChange);
    // Listen for custom 'loaded' event when translations are added
    if (typeof i18n.on === 'function') {
      i18n.on('loaded', handleTranslationsLoaded);
    }
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
      if (typeof i18n.off === 'function') {
        i18n.off('loaded', handleTranslationsLoaded);
        i18n.off('initialized', () => {});
      }
    };
  }, [currentLanguage]);

  return {
    t,
    changeLanguage: async (lang) => {
      await changeLanguage(lang);
    },
    language: currentLanguage,
  };
};
